import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  Injector,
} from '@angular/core';
import { Observable, Subscription } from 'rxjs';
// import { Privilege, RolePrivilege, RolePrivilegePending } from 'src/app/_interface/RolePrivilege';
import { MatDialogConfig } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { YesNoDialogComponent } from 'src/app/controls/yes-no-dialog/yes-no-dialog.component';
import { RolePrivilegeService } from 'src/app/services/role-privilege/role-privilege.service';
import { Role } from 'src/app/components/catalogs/roles/services/role';
import {
  RolePrivilegePending,
  RolePrivilege,
  Privilege,
  PrivilegeCategory,
} from 'src/app/components/catalogs/roles/services/role-privilege';
import { RolePrivilegeAddComponent } from './role-privilege-add/role-privilege-add.component';
import { BaseComponent } from 'src/app/common/base/base.component';
import { utils } from 'src/app/modules/libs/utils';
import { PrivilegeCategoryRefresh } from 'src/app/services/role-privilege/store/privilege-categories/privilege-categories.action';
import { PrivilegeEnum } from 'src/app/common/enumerations/privilege-enum';

@Component({
  selector: 'role-privilege',
  templateUrl: './role-privilege.component.html',
  styleUrls: ['./role-privilege.component.scss'],
})
export class RolePrivilegeComponent
  extends BaseComponent
  implements OnInit, OnDestroy, OnChanges {
  @Input() role?: Role | null;
  @Input() modifying!: boolean;
  @Input() showChanges!: boolean;

  @Output() rolePrivilegesPendingChanged = new EventEmitter<
    RolePrivilegePending[]
  >();

  rolePrivilege!: RolePrivilege;
  rolePrivileges!: RolePrivilege[];
  rolePrivilegesOriginal!: RolePrivilege[];

  privilege!: Privilege;
  privileges!: Privilege[];
  privileges$!: Observable<Privilege[]>;
  privilegesSubs!: Subscription;

  documentTypes!: DocumentType[];
  documentTypes$!: Observable<DocumentType[]>;
  documentTypesSubs!: Subscription;

  privilegeCategories?: PrivilegeCategory[] = [];

  showChangesToggle!: boolean;

  public displayedColumns: string[] = ['type', 'privileges', 'action'];

  public dataSource: MatTableDataSource<PrivilegeCategory> =
    new MatTableDataSource();

  constructor(
    protected override injector: Injector,
    private service: RolePrivilegeService,
  ) {
    super(injector);
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.loadRolePrivileges();
  }

  override ngOnDestroy(): void {
    this.privilegesSubs?.unsubscribe();
    this.documentTypesSubs?.unsubscribe();
  }

  ngOnInit(): void {
    this.loadPrivileges();
    this.loadDocumentTypes();
    this.store.dispatch(new PrivilegeCategoryRefresh());
  }

  loadPrivileges() {
    this.privileges$ = this.store.select((state) => state.Privileges.data);
    this.privilegesSubs = this.privileges$.subscribe((data) => {
      this.privileges = data;
    });
  }
  /**
   * Loads role privileges, enriches them with privilege objects and pending change indicators, then updates the data source.
   * Mutates rolePrivileges array items by adding privilegeObj, pending, and pendingClass properties.
   */ loadRolePrivileges() {
    this.rolePrivileges = this.utils.cloneDeep(this.role?.rolePrivileges ?? []);

    this.rolePrivileges.map((r) => {
      const privilegeObj = this.privileges.find((p) => p.id == r.privilege);
      if (privilegeObj) {
        r.privilegeObj = privilegeObj;
      }
      const pending = this.role?.rolePrivilegesPending?.find(
        (p) => p.privilege == r.privilege,
      );
      if (pending && this.showChanges) {
        r.pending = true;
        r.pendingClass = pending.selected ? 'yellow-bg' : 'red-bg';
      }
    });
    this.showChangesPending();
    this.createDataSource();
  }

  createDataSource() {
    this.rolePrivileges = this.rolePrivileges
      .filter((x) => x.privilegeObj)
      .sort(
        utils.sortByMultipleFields<RolePrivilege>([
          'privilegeObj.privilegeCategory.name',
          'privilegeObj.name',
        ]),
      );
    this.rolePrivilegesOriginal = this.rolePrivileges;
    this.rolePrivileges.forEach((rolePrivilegeNew) => {
      if (
        !this.privilegeCategories
          ?.map((c) => c.id)
          .includes(rolePrivilegeNew.privilegeObj?.privilegeCategoryID)
      ) {
        this.privilegeCategories?.push(
          rolePrivilegeNew.privilegeObj!.privilegeCategory,
        );
      }
    });
    this.privilegeCategories?.forEach((c) => {
      c.rolePrivileges =
        this.rolePrivileges.filter(
          (p) => p.privilegeObj?.privilegeCategoryID == c.id,
        ) ?? [];
    });
    this.dataSource = new MatTableDataSource(
      this.privilegeCategories?.filter((c) => c.rolePrivileges?.length),
    );
  }

  loadDocumentTypes() {
    this.documentTypes$ = this.store.select((state) => state.DocumentType.data);
    this.documentTypesSubs = this.documentTypes$.subscribe((data) => {
      this.documentTypes = data;
    });
  }

  getRowSpan(e: RolePrivilege) {
    return this.rolePrivileges.filter(
      (x) =>
        x.privilegeObj?.privilegeCategoryID ==
        e.privilegeObj?.privilegeCategoryID,
    ).length;
  }

  isFirstElem(e: RolePrivilege) {
    const group = this.rolePrivileges.filter(
      (x) =>
        x.privilegeObj?.privilegeCategoryID ==
        e.privilegeObj?.privilegeCategoryID,
    );
    return group.findIndex((x) => x.privilege == e.privilege) == 0;
  }

  openRolePrivilegeDialog(categoryId?: number) {
    const dialogData = {
      role: this.role,
      rolePrivileges: this.rolePrivileges,
      categoryId,
    };
    const dialogConfig = new MatDialogConfig();
    dialogConfig.autoFocus = false;
    dialogConfig.width = '400px';
    dialogConfig.data = dialogData;
    const dialogRef = this.dialog.open(RolePrivilegeAddComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((data: RolePrivilegePending[]) => {
      if (data.length && this.role) {
        data.forEach((rpp) => {
          rpp.roleID = this.role!.id!;
          const existing = this.role!.rolePrivilegesPending?.find(
            (x) => x.privilege == rpp.privilege,
          );
          if (existing && existing.selected != rpp.selected) {
            existing.selected = rpp.selected;
          } else {
            this.role!.rolePrivilegesPending =
              this.role!.rolePrivilegesPending || [];
            this.role!.rolePrivilegesPending.push(rpp);
          }
        });

        this.rolePrivilegesPendingChanged.emit(this.role.rolePrivilegesPending);
        this.showChanges = true;
        this.role.rolePrivilegesPending?.forEach((rpp) => {
          if (
            !this.rolePrivileges
              .map((rp) => rp.privilege)
              .includes(rpp.privilege)
          ) {
            const rolePrivilegeNew = {
              id: 0,
              roleID: this.role?.id,
              privilege: rpp.privilege,
              privilegeObj: this.privileges.find(
                (p) => p.privilegeEnum == rpp.privilege,
              ),
              role: this.role,
              status: 1,
              pending: true,
              pendingClass: rpp.selected ? 'yellow-bg' : 'red-bg',
            } as RolePrivilege;
            this.rolePrivileges?.push(rolePrivilegeNew);
            if (
              !this.privilegeCategories
                ?.map((c) => c.id)
                .includes(rolePrivilegeNew.privilegeObj?.privilegeCategoryID)
            ) {
              this.privilegeCategories?.push(
                rolePrivilegeNew.privilegeObj!.privilegeCategory,
              );
            }
          }
        });
        this.createDataSource();
        this.showChangesPending();
      }
    });
  }

  showChangesPending() {
    if (this.role?.rolePrivilegesPending?.length) {
      this.role.rolePrivilegesPending.forEach((rpp) => {
        const rp = this.rolePrivileges.find(
          (rp) => rp.privilege == rpp.privilege,
        );
        if (!rp) {
          const rolePrivilegeNew = {
            id: 0,
            roleID: this.role?.id,
            privilege: rpp.privilege,
            privilegeObj: this.privileges.find(
              (p) => p.privilegeEnum == rpp.privilege,
            ),
            role: this.role,
            status: 1,
            pending: true,
            pendingClass: rpp.selected ? 'yellow-bg' : 'red-bg',
          } as RolePrivilege;
          this.rolePrivileges?.push(rolePrivilegeNew);
        } else {
          rp.pending = true;
          rp.pendingClass = rpp.selected ? 'yellow-bg' : 'red-bg';
        }
      });

      this.rolePrivileges = this.rolePrivileges.sort(
        utils.sortByMultipleFields<RolePrivilege>([
          'privilegeObj.privilegeCategory.name',
          'privilegeObj.name',
        ]),
      );
      this.createDataSource();
    }
  }
}
